import { Component, effect, inject, OnDestroy } from '@angular/core';
import { addIcons } from 'ionicons';
import * as ionIcons from 'ionicons/icons';
import { ThemeModule } from '@app/modules';
import { UserModel } from '@app/models';
import {
    AlertService,
    AppInfoService,
    AuthService,
    LoadingService,
    SqliteService,
    WebSocketService
} from '@app/services';
import { Capacitor } from '@capacitor/core';
import { Device } from '@capacitor/device';
import { Platform } from '@ionic/angular/standalone';
import { RouteRepository, UserRepository } from '@app/repositories';
import { Router } from '@angular/router';
import * as Sentry from '@sentry/capacitor';

@Component({
    standalone: true,
    selector: 'app-root',
    templateUrl: 'app.component.html',
    styleUrls: ['app.component.scss'],
    imports: [ThemeModule],
})
export class AppComponent implements OnDestroy
{
    user: UserModel | null = null;
    appPages = [
        { title: 'Home', url: '/tabs/home', icon: 'home', },
        // { title: 'Active Route', url: '/tabs/active-route', icon: 'map', },
    ];
    appInfo: string | null = null;
    authService = inject(AuthService);
    appInfoService = inject(AppInfoService);

    private platform = inject(Platform);
    private loadingService = inject(LoadingService);
    private userRepository = inject(UserRepository);
    private router = inject(Router);
    private sqliteService = inject(SqliteService);
    private routeRepository = inject(RouteRepository);
    private alertService = inject(AlertService);
    private webSocketService = inject(WebSocketService);

    constructor()
    {
        addIcons(ionIcons);

        // Effect to react to isLoggedIn signal changes
        effect(async () =>
        {
            const isLoggedIn = this.authService.isLoggedIn();

            if (isLoggedIn)
            {
                this.user = await this.userRepository.getLoggedInUser();
            }
            else
            {
                this.user = null;
            }

            if (this.user)
            {
                Sentry.setUser({
                    id: this.user.id,
                    username: this.user.external_id,
                });

                await this.webSocketService.listenForForceLogoutEvents();
            }
            else
            {
                Sentry.setUser(null);
            }
        });

        this.platform.ready().then(async () =>
        {
            const deviceInfo = await Device.getInfo();
            console.info('await Device.getInfo()', deviceInfo);

            this.appInfo = await this.appInfoService.getAppInfo();
            console.info('this.appInfo', this.appInfo);

            Sentry.setTag('app_info', this.appInfo);
            Sentry.setTag('platform', Capacitor.getPlatform());
            Sentry.setTag('webview_version', deviceInfo.webViewVersion);
            Sentry.setTag('os_version', deviceInfo.osVersion);
            Sentry.setTag('device_model', deviceInfo.model);
            Sentry.setTag('mem_used', deviceInfo.memUsed);
        });
    }

    async ngOnDestroy()
    {
        Sentry.addBreadcrumb({
            category: 'sqlite',
            message: 'Closing existing connection from ngOnDestroy',
            level: 'info',
        });
        await this.sqliteService.closeConnection();
    }

    async logout()
    {
        const [overdueRoute, activeRoute,] = await Promise.all([
            this.routeRepository.getOverdueRoute(),
            this.routeRepository.getActiveRoute(),
        ]);

        if (overdueRoute !== null || activeRoute !== null)
        {
            await this.alertService.show('You have an active route. Please finish it before logging out.', 6000);

            return;
        }

        try
        {
            await this.loadingService.show('Logging out...');

            await this.authService.logout();
        }
        finally
        {
            await this.sqliteService.deleteDatabase();
            await this.userRepository.logout();

            this.webSocketService.unsubscribeFromAll();

            this.authService.isLoggedIn.set(false);
            await this.router.navigate(['/login'], { replaceUrl: true, });

            await this.loadingService.hide();
        }
    }
}
